<template>
  <div class="card is-width-1440px pa-20">
    <header class="card-title has-text-justified">
      <p class="has-text-grid-1 has-text-weight-bold is-size-6">
        {{$t('ajoutProduitExistant.modalAddTitle')}}
      </p>
    </header>
    <section class="py-10">
      <b-table
        :data="items"
        :loading="loading"
        :checked-rows="itemsSelected"
        :is-row-checkable="isRowCheckable"
        :header-checkable="false"
        @check="onCheck"
        @sort="onSort"
        class="custom-table sticky-header"
        backend-sorting
        sticky-header
        checkable>
        <b-table-column
          v-for="column in constants.COLUMNS"
          :key="column.id"
          :label="column.sortable ? `↑↓ ${$t(column.label)}` : $t(column.label)"
          v-bind="column">

          <template #searchable>
            <qd-filter-table-row
              v-model="filter[column.searchField]"
              :column="column"
              @reloadItemsForSearch="reloadItemsForSearch"/>
          </template>

          <template v-slot="{ row }">
            <figure v-if="column.field === 'urlLogo'">
              <img :src="func.getImg(row.urlLogo)" class="is-size-30px">
            </figure>
            <div v-else-if="column.field === 'etatIcone'">
              <b-tooltip
                v-if="row.infoDateDemontage"
                :label="row.infoDateDemontage"
                position='is-right'>
                <b-icon
                  icon="sign-caution"
                  type="is-warning">
                </b-icon>
              </b-tooltip>
            </div>
            <span v-else>
              {{ getValueColumn(row, column) }}
            </span>
          </template>

        </b-table-column>
        <template #empty>
          <section class="section">
            <div class="content has-text-grey has-text-centered">
              <p> {{$t('general.autocomplete.noRslt')}} </p>
            </div>
          </section>
        </template>
      </b-table>
      <qd-pagination
        :page="page"
        :perPage="perPage"
        :total="totalCount"
        @page-change="reloadItemsForPage($event, perPage)"
        @perPage-change="reloadItemsForPage(1, $event)"
      />
    </section>
    <footer class="buttons">
      <b-button
        type="is-transparent"
        class="has-text-default"
        @click="$parent.close()">
        {{$t('general.btnAnnuler')}}
      </b-button>
      <b-button
        @click="addProduitToRecom"
        :disabled="!itemsSelected.length"
        type="is-primary"
        class="has-text-weight-bold ml-20">
        {{$t('general.btnAppliquer')}}
      </b-button>
    </footer>
  </div>
</template>
<script>
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import { createNamespacedHelpers } from 'vuex';
import { GetAllProduitFaceForRecom } from '@/api/produit/index';
import { COLUMNS } from '@/configuration/listProduitsConfig';
import QdPagination from '@/components/general/qdPagination.vue';
import QdFilterTableRow from '@/components/general/qdFilterTableRow.vue';
import { getImg } from '@/services/general/general';
import gestionErreur from '@/services/general/gestionErreur';
import { getIconPanneau } from '@/services/gestionIcon/iconColorGetter';
import { transformProduitToPanneau } from '@/services/gestionPanneau/genererPanneau';
import getPiecesJointeLogoByIds from '@/services/piecesJointes/getPiecesJointeLogoByIds';
import { genererFilter, transformFilter } from '@/services/recommandation/ajoutProduitExistant';
import { produitFaceForRecomToProduitFaceMapper } from '@/mapping/produit.mapping';
import { majFormatIdProduit } from '@/services/creationProduit/produitFace';
import { PARAMETRES_KEYS } from '@/store/modules/parametrage-mutation-type';
import { SET_PANNEAU_EXISTANT } from '@/store/modules/recommandations-mutation-type';

const Parametres = createNamespacedHelpers('parametrage');
const Recommandations = createNamespacedHelpers('recommandations');

export default {
  components: {
    QdPagination,
    QdFilterTableRow,
  },
  data() {
    return {
      page: 1,
      perPage: 10,
      totalCount: 0,
      loading: false,
      sortOrder: 'desc',
      sortField: 'creationTime',
      items: [],
      itemsSelected: [],
      func: { getImg },
      constants: { COLUMNS },
      filter: genererFilter(),
    };
  },
  computed: {
    ...Recommandations.mapGetters({
      panneaux: 'panneaux',
      pointDeVente: 'pointDeVente',
    }),
  },
  methods: {
    ...Recommandations.mapMutations({
      setPanneauExistant: SET_PANNEAU_EXISTANT,
    }),
    ...Parametres.mapActions({
      loadParametres: 'loadParametres',
    }),
    reloadItemsForSearch: debounce(async function reloadFunc() {
      await this.loadItems();
    }, 1000),
    isRowCheckable(row) {
      return !this.panneaux.some(el => row.id === el.id);
    },
    getValueColumn(row, column) {
      if (column.format) return column.format(row);
      return get(row, column.field);
    },
    onSort(field, order) {
      this.sortField = field;
      this.sortOrder = order;
      this.loadItems();
    },
    onCheck(_, row) {
      if (this.itemsSelected.length && this.itemsSelected[0].id === row.id) {
        this.itemsSelected = [];
        return;
      }
      this.itemsSelected = [row];
    },
    async reloadItemsForPage(page, perPage) {
      this.page = page;
      this.perPage = perPage;
      await this.loadItems();
    },
    async loadItems() {
      this.loading = true;
      const payload = {
        query: transformFilter(this.filter),
        page: this.page,
        limit: this.perPage,
        ascending: this.sortOrder === 'asc',
        orderBy: this.sortField,
      };
      try {
        const { items, totalCount } = (await GetAllProduitFaceForRecom(payload)).data.result;
        const itemsMapper = items.map(produitFaceForRecomToProduitFaceMapper);
        await getPiecesJointeLogoByIds(itemsMapper);
        this.items = itemsMapper;
        this.totalCount = totalCount;
      } catch (error) {
        gestionErreur(this.$t('errors.erreurRecuperationProduitsFace'));
        throw error;
      } finally {
        this.loading = false;
      }
    },
    addProduitToRecom() {
      const p = this.itemsSelected[0];
      majFormatIdProduit(p);
      const pan = transformProduitToPanneau(p, p.tiers);
      pan.icon = getIconPanneau(pan);
      pan.srcImg = p.urlLogo;
      pan.urlLogo = p.urlLogo;
      this.setPanneauExistant(pan);
      this.$parent.close();
      this.$router.push({ name: 'creer-panneau' });
    },
  },
  async mounted() {
    await this.loadParametres([PARAMETRES_KEYS.materielFormat]);
    await this.loadItems();
  },
};
</script>
